import { StorageService } from './../../../common/services/storage.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AssociationService } from '../../../common/services/association.service';
import { Association, RegisterAssociation, UpdateAssociation } from '../../../common/models/association';
import { CurrentUser } from '../../../common/models/user';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-association-detail',
  templateUrl: './association.component.html',
  styleUrls: ['./association.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatSelectModule
]
})
export class AssociationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  associationBasicInfoForm: FormGroup;
  associationHeadquartersForm: FormGroup;
  associationDetailsForm: FormGroup;
  userFormGroup: FormGroup;

  isUpdating: boolean = false;
  isLoading: boolean = true;
  associationId!: string;

  user: CurrentUser | null = null;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };


  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private associationService: AssociationService,
    private snackBar: MatSnackBar,
    private router: Router,
    private storage: StorageService,
    private breakpointObserver: BreakpointObserver
  ) {
    // Step 1: Osnovni podaci
    this.associationBasicInfoForm = this.fb.group({
      name: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      oib: [null, Validators.required],
      rno: [null, Validators.required],
      yearOfEstablishment: [null, Validators.required],
      dateOfRegistration: [null, Validators.required],
      registrationNumber: [null, Validators.required],
      registeredAuthority: [null, Validators.required],
      bankName: [null, Validators.required],
      bankAccountNumber: [null, Validators.required],
      contactNumber: [null, Validators.required],
      web: [null, Validators.required]
    });

    // Step 2: Podaci o sjedištu
    this.associationHeadquartersForm = this.fb.group({
      county: [null, Validators.required],
      postalCodeAndHeadquarters: [null, Validators.required],
      address: [null, Validators.required]
    });

    // Step 3: Dodatni podaci
    this.associationDetailsForm = this.fb.group({
      objectivesAccordingToStatute: [null, Validators.required],
      purposeAndFieldOfOperation: [null, Validators.required],
      activitiesAccordingToStatute: [null, Validators.required]
    });

    // Step 4 (create only): User data
    this.userFormGroup = this.fb.group({
      userName: [null, Validators.required], // Typically email
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      password: [null, [Validators.required, Validators.minLength(8)]], // required only on create
      duty: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      this.isHandset = result.matches;
    });


    this.user = this.storage.getUser();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isUpdating = true;
      this.associationId = idParam;
      this.loadAssociation(this.associationId);
      this.disableFields();
    } else {
      // Create scenario
      this.isUpdating = false;
      this.isLoading = false;
    }
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  disableFields(){
    this.associationBasicInfoForm.get('email')?.disable();
    this.associationBasicInfoForm.get('oib')?.disable();
    this.associationBasicInfoForm.get('rno')?.disable();
    this.associationBasicInfoForm.get('registrationNumber')?.disable();
  }

  loadAssociation(id: string) {
    this.associationService.getAssociation(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (association: Association) => {
        // Patch forms
        this.associationBasicInfoForm.patchValue({
          name: association.name,
          email: association.email,
          oib: association.oib,
          rno: association.rno,
          yearOfEstablishment: association.yearOfEstablishment,
          dateOfRegistration: association.dateOfRegistration,
          registrationNumber: association.registrationNumber,
          registeredAuthority: association.registeredAuthority,
          bankName: association.bankName,
          bankAccountNumber: association.bankAccountNumber,
          contactNumber: association.contactNumber,
          web: association.web
        });

        this.associationHeadquartersForm.patchValue({
          county: association.county,
          postalCodeAndHeadquarters: association.postalCodeAndHeadquarters,
          address: association.address
        });

        this.associationDetailsForm.patchValue({
          objectivesAccordingToStatute: association.objectivesAccordingToStatute,
          purposeAndFieldOfOperation: association.purposeAndFieldOfOperation,
          activitiesAccordingToStatute: association.activitiesAccordingToStatute
        });

        // In update scenario we do not modify userFormGroup (it remains disabled/unused)
        this.userFormGroup.disable();
        
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading association', err);
        this.isLoading = false;
      }
    });
  }

  onSubmit() {
    if (this.isUpdating) {
      this.updateAssociation();
    } else {
      this.createAssociation();
    }
  }

  createAssociation() {
    if (this.associationBasicInfoForm.invalid ||
        this.associationHeadquartersForm.invalid ||
        this.associationDetailsForm.invalid ||
        this.userFormGroup.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig);
      return;
    }

    // Gather all association values
    const basicInfoValues = this.associationBasicInfoForm.value;
    const headquartersValues = this.associationHeadquartersForm.value;
    const additionalValues = this.associationDetailsForm.value;
    const userValues = this.userFormGroup.value;

    const dto: RegisterAssociation = {
      municipalityId: this.user?.municipalityId ?? '',
      userName: userValues.userName,
      firstName: userValues.firstName,
      lastName: userValues.lastName,
      password: userValues.password,
      associationName: basicInfoValues.name,
      associationEmail: basicInfoValues.email,
      objectivesAccordingToStatute: additionalValues.objectivesAccordingToStatute,
      purposeAndFieldOfOperation: additionalValues.purposeAndFieldOfOperation,
      activitiesAccordingToStatute: additionalValues.activitiesAccordingToStatute,
      oib: basicInfoValues.oib,
      contactNumber: basicInfoValues.contactNumber,
      address: headquartersValues.address,
      postalCodeAndHeadquarters: headquartersValues.postalCodeAndHeadquarters,
      county: headquartersValues.county,
      web: basicInfoValues.web,
      yearOfEstablishment: basicInfoValues.yearOfEstablishment,
      dateOfRegistration: basicInfoValues.dateOfRegistration,
      registrationNumber: basicInfoValues.registrationNumber,
      registeredAuthority: basicInfoValues.registeredAuthority,
      bankAccountNumber: basicInfoValues.bankAccountNumber,
      bankName: basicInfoValues.bankName,
      rno: basicInfoValues.rno,
      duty: userValues.duty
    };

    this.associationService.registerAssociation(dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste kreirali udrugu i odgovornu osobu.', 'Zatvori', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
        this.router.navigate(['municipality', 'associations']);
      },
      error: (error) => {
        console.error('Error creating association', error);
        this.snackBar.open('Greška prilikom kreiranja udruge', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  updateAssociation() {
    if (this.associationBasicInfoForm.invalid ||
        this.associationHeadquartersForm.invalid ||
        this.associationDetailsForm.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig);
      return;
    }

    const basicInfoValues = this.associationBasicInfoForm.value;
    const headquartersValues = this.associationHeadquartersForm.value;
    const additionalValues = this.associationDetailsForm.value;

    const dto: UpdateAssociation = {
      id: this.associationId,
      name: basicInfoValues.name,
      objectivesAccordingToStatute: additionalValues.objectivesAccordingToStatute,
      purposeAndFieldOfOperation: additionalValues.purposeAndFieldOfOperation,
      activitiesAccordingToStatute: additionalValues.activitiesAccordingToStatute,
      contactNumber: basicInfoValues.contactNumber,
      address: headquartersValues.address,
      postalCodeAndHeadquarters: headquartersValues.postalCodeAndHeadquarters,
      county: headquartersValues.county,
      web: basicInfoValues.web,
      yearOfEstablishment: basicInfoValues.yearOfEstablishment,
      dateOfRegistration: basicInfoValues.dateOfRegistration,
      registeredAuthority: basicInfoValues.registeredAuthority,
      bankAccountNumber: basicInfoValues.bankAccountNumber,
      bankName: basicInfoValues.bankName
    };

    this.associationService.updateAssociation(dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste ažurirali podatke o udruzi.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['municipality', 'associations']);
      },
      error: (error) => {
        console.error('Error updating association', error);
        this.snackBar.open('Greška prilikom ažuriranja udruge', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}